/**
 * ConfirmAction — the two-step gate on every consequential admin action.
 *
 * EVERY action behind this component writes an `audit_log` row server-side, permanently, against
 * the signed-in staff member's id. Several also send a real person an email they did not ask for
 * (reject), or re-drive an AI pipeline (approve), or re-open contact with someone who asked to be
 * left alone (un-suppress). None of them should be one stray click away.
 *
 * Deliberately INLINE rather than an overlay modal. Three reasons, in order of weight:
 *   1. An inline confirm needs no focus trap. A trap implemented by hand is a well-known way to
 *      strand keyboard and screen-reader users, and this console is worked by keyboard.
 *   2. It keeps the row it belongs to on screen, so the reviewer confirms against the thing
 *      itself rather than a dialog that has covered it.
 *   3. It costs no dependency.
 *
 * Accessibility: the revealed region is a `role="alertdialog"` labelled by its own heading and
 * described by its body; focus moves to the confirm button on open and back to the trigger on
 * cancel; Escape cancels. The confirm button is never the default focus of the page and never
 * auto-fires.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, type ButtonProps } from './ui/button';
import { cn } from '@/lib/utils';

interface ConfirmActionProps {
  /** The resting label, e.g. "Approve". */
  label: string;
  triggerVariant?: ButtonProps['variant'];
  triggerSize?: ButtonProps['size'];
  disabled?: boolean;
  /** Why the trigger is disabled — rendered as the button's title and to assistive tech. */
  disabledReason?: string;
  /** Short question, e.g. "Approve and re-draft this reference?". */
  title: string;
  /** What will actually happen. Say it plainly, including what the other person receives. */
  children: ReactNode;
  confirmLabel: string;
  confirmVariant?: ButtonProps['variant'];
  pending?: boolean;
  onConfirm: () => void;
  className?: string;
}

export function ConfirmAction({
  label,
  triggerVariant = 'ghost',
  triggerSize = 'sm',
  disabled = false,
  disabledReason,
  title,
  children,
  confirmLabel,
  confirmVariant = 'default',
  pending = false,
  onConfirm,
  className,
}: ConfirmActionProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const restorePending = useRef(false);
  const headingId = useId();
  const bodyId = useId();

  const close = useCallback(() => {
    // Only FLAG the restore here. The trigger is not in the tree while the confirm is open (this
    // component renders one or the other), so `triggerRef.current` is still null at this point —
    // calling focus() on it now is a no-op and the keyboard user gets dumped on <body>. The
    // effect below runs after the re-render that mounts the trigger, which is the first moment
    // there is anything to focus.
    restorePending.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      // Focus the confirm button when the region opens, so the keyboard path is one Tab shorter
      // and a screen reader announces the alertdialog's name and description immediately.
      confirmRef.current?.focus();
    } else if (restorePending.current) {
      restorePending.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (!open) {
    const blockedId = `${bodyId}-blocked`;
    return (
      <>
        <Button
          ref={triggerRef}
          variant={triggerVariant}
          size={triggerSize}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          aria-describedby={disabled && disabledReason ? blockedId : undefined}
          onClick={() => setOpen(true)}
          className={className}
        >
          {label}
        </Button>
        {/* The reason sits OUTSIDE the button, referenced by aria-describedby. Nesting it as a
            child would fold it into the button's accessible NAME — the button would announce as
            "Approve There are no key points to draft…" instead of "Approve", described by the
            reason. A name is what the control is; a description is why it is unavailable. */}
        {disabled && disabledReason && (
          <span id={blockedId} className="sr-only">
            {disabledReason}
          </span>
        )}
      </>
    );
  }

  return (
    <div
      role="alertdialog"
      aria-labelledby={headingId}
      aria-describedby={bodyId}
      className={cn(
        'w-full rounded-md border border-line-2 bg-surface-tint p-3.5 shadow-sm',
        className,
      )}
    >
      <p id={headingId} className="font-display text-[13.5px] font-semibold text-ink">
        {title}
      </p>
      <div id={bodyId} className="mt-1.5 text-[12.5px] leading-relaxed text-ink-70">
        {children}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          ref={confirmRef}
          variant={confirmVariant}
          size="sm"
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? 'Working…' : confirmLabel}
        </Button>
        <Button variant="ghost" size="sm" disabled={pending} onClick={close}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
